package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.Friend
import fyi.moko.hotpl.domain.model.Friendship
import fyi.moko.hotpl.domain.model.canonicalFriendshipId
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.SocialGraphService
import kotlin.time.Clock

/**
 * Redeems a friend's invite code, establishing the mutual friendship.
 * Domain-level rejections come back as [RedeemInviteResult.Rejected]; transport
 * failures (offline, rules denial) propagate as exceptions for the caller's
 * generic error path.
 */
sealed interface RedeemInviteResult {
    data class Redeemed(val friend: Friend) : RedeemInviteResult
    data class Rejected(val reason: RedeemInviteRejection) : RedeemInviteResult
}

enum class RedeemInviteRejection {
    /** No invite code doc with that id. */
    InvalidCode,

    /** The presented code is the caller's own. */
    SelfFriend,

    /** A friendship between the two users already exists. */
    AlreadyFriends,
}

class RedeemInviteCodeUseCase(
    private val authService: AuthService,
    private val socialGraphService: SocialGraphService,
    private val clock: Clock = Clock.System,
) {
    suspend operator fun invoke(rawCode: String): RedeemInviteResult {
        val code = rawCode.trim().uppercase()
        if (code.isEmpty()) return RedeemInviteResult.Rejected(RedeemInviteRejection.InvalidCode)

        val myUid = authService.ensureSignedIn()
        val invite = socialGraphService.getInviteCode(code)
            ?: return RedeemInviteResult.Rejected(RedeemInviteRejection.InvalidCode)
        if (invite.ownerUid == myUid) {
            return RedeemInviteResult.Rejected(RedeemInviteRejection.SelfFriend)
        }

        val friendshipId = canonicalFriendshipId(myUid, invite.ownerUid)
        if (socialGraphService.getFriendship(friendshipId) != null) {
            return RedeemInviteResult.Rejected(RedeemInviteRejection.AlreadyFriends)
        }

        val myName = authService.displayName()
            ?: authService.linkedEmail()
            ?: Friendship.ANONYMOUS_NAME
        val friendship = Friendship(
            id = friendshipId,
            uids = listOf(myUid, invite.ownerUid).sorted(),
            names = mapOf(myUid to myName, invite.ownerUid to invite.ownerName),
            inviteCode = code,
            createdAt = clock.now().toEpochMilliseconds(),
        )
        socialGraphService.createFriendship(friendship)
        return RedeemInviteResult.Redeemed(friendship.toFriend(myUid))
    }
}
